import { Settings, VisibilityOff } from '@mui/icons-material';
import { Box, Button, IconButton, Paper, Stack, Typography, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import type { IEmployeeSuggestedCompetence } from '@sharedInterfaces/IEmployee';
import { getLanguageTexts } from '@sharedInterfaces/Language/languageHelper';
import getSuggestedCompetences from '@src/APIs/graphQl/Employee/Suggestions/getSuggestedCompetences';
import hideSuggestedCompetence from '@src/APIs/graphQl/Employee/Suggestions/hideSuggestedCompetence';
import { useDialog } from '@src/App/Components/Dialogs/DialogManager';
import { EmployeeCoverage } from '@src/App/Components/General/EmployeeSuggestion/EmployeeSuggestions/EmployeeCoverage';
import { setHiddenCompetences } from '@store/reducer/employeeSettingsReducer';
import type { AppState } from '@store/store';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import ExpandableText from '../../General/ExpandableText/ExpandableText';
import FormatedTextInput from '../../General/FormularElements/formsControls/inputs/FormatedTextInput/FormatedTextInput';
import { ConfigureWantedCometenceDialog } from './ConfigureWantedCometenceDialog';
import { HiddenCompetencesDialog } from './HiddenCompetencesDialog';

/**
 * Represents the availability settings component.
 *
 * @returns {JSX.Element}
 */
function SuggestedCompetences() {
    const theme = useTheme();
    const { openDialog } = useDialog();
    const lang = useSelector((state: AppState) => state.employee.language);
    const langStrings = getLanguageTexts(lang).competence;
    const offline = useSelector((state: AppState) => state.client.offline);
    const [competences, setCompetences] = React.useState<IEmployeeSuggestedCompetence[]>([]);
    const [hiddenCompetencesDialog, setHiddenCompetencesDialog] = React.useState<boolean>(false);

    const updateSuggestions = () => {
        getSuggestedCompetences((suggestedCompetences) => {
            setCompetences(suggestedCompetences);
        });
    };

    React.useEffect(() => {
        if (hiddenCompetencesDialog === false) {
            updateSuggestions();
        } else {
            openDialog(
                <HiddenCompetencesDialog
                    id="HiddenCompetencesDialog"
                    resolve={setHiddenCompetencesDialog.bind(null, false)}
                />,
            );
        }
    }, [hiddenCompetencesDialog]);

    return (
        <Paper
            elevation={0}
            sx={{
                p: 2,
                borderRadius: 2,
                border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                bgcolor: 'background.paper',
                mb: 2,
            }}
        >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {langStrings.suggestedCompetences}
                </Typography>
                <IconButton onClick={setHiddenCompetencesDialog.bind(null, true)} disabled={offline} size="small">
                    <Settings />
                </IconButton>
            </Box>

            <Typography variant="subtitle2" sx={{ mb: 2, color: 'text.secondary' }}>
                {langStrings.suggestionCardHelpText}
            </Typography>

            <Stack spacing={2}>
                {competences.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                        {langStrings.oftenUsed.empty}
                    </Typography>
                ) : (
                    competences.map((cp) => (
                        <SuggestedCompetence key={cp.id} competence={cp} update={updateSuggestions} />
                    ))
                )}
            </Stack>
        </Paper>
    );
}
export default SuggestedCompetences;

/**
 * Generate a suggested competence element.
 * @param {competence} - The competence object.
 * @returns {JSX.Element} - The suggested competence element.
 */
export function SuggestedCompetence({
    competence,
    update,
}: {
    competence: IEmployeeSuggestedCompetence;
    update: () => void;
}) {
    const theme = useTheme();
    const { openDialog } = useDialog();
    const dispatch = useDispatch();
    const lang = useSelector((state: AppState) => state.employee.language);
    const langStrings = getLanguageTexts(lang).competence;
    const allCompetences = useSelector((state: AppState) => state.company.allCompetences);
    const hiddenCompetences = useSelector(
        (state: AppState) => state.employeeSettings.competenceSettings.hiddenCompetences,
    );
    const wantedCompetences = useSelector(
        (state: AppState) => state.employeeSettings.competenceSettings.wantedCompetences,
    );
    const offline = useSelector((state: AppState) => state.client.offline);

    const allCompetence = allCompetences.find((a) => a.id === competence.id);
    const onHide = () => {
        hideSuggestedCompetence(competence.id);
        const newHiddenProjects = hiddenCompetences.concat();
        newHiddenProjects.push(competence.id);
        dispatch(setHiddenCompetences(newHiddenProjects));
    };
    const onConfigure = () => {
        openDialog(
            <ConfigureWantedCometenceDialog
                id="ConfigureWantedCometenceDialog"
                competence={competence}
                resolve={() => {
                    update();
                }}
            />,
        );
    };
    if (
        !allCompetence ||
        hiddenCompetences.includes(competence.id) ||
        wantedCompetences.find((wc) => wc.id === competence.id && wc.level === competence.level)
    )
        return <></>;

    return (
        <Paper
            elevation={0}
            sx={{
                p: 2,
                borderRadius: 2,
                border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                bgcolor: 'background.paper',
            }}
        >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'primary.main' }}>
                    {`${competence.title} - ${allCompetence.levels[competence.level - 1].title}`}
                </Typography>
                <IconButton
                    title={langStrings.hideSuggestedCompetenceHelper}
                    onClick={onHide}
                    disabled={offline}
                    size="small"
                >
                    <VisibilityOff />
                </IconButton>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <ExpandableText
                    extraHiddenChildren={
                        competence.employee ? (
                            <Box sx={{ pb: 1 }}>
                                <EmployeeCoverage
                                    expended
                                    suggestedEmployee={competence.employee}
                                    hideName
                                    hideAvailability
                                />
                            </Box>
                        ) : undefined
                    }
                >
                    {competence.description && competence.description !== '' ? (
                        <FormatedTextInput value={competence.description} readonly={true} />
                    ) : undefined}
                </ExpandableText>

                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Button
                        variant="outlined"
                        startIcon={<VisibilityOff />}
                        onClick={onHide}
                        disabled={offline}
                        size="small"
                        sx={{ textTransform: 'none', flex: 1, minWidth: 'fit-content' }}
                    >
                        {langStrings.hideSuggestedCompetence}
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={<Settings />}
                        onClick={onConfigure}
                        disabled={offline}
                        size="small"
                        sx={{ textTransform: 'none', flex: 1, minWidth: 'fit-content' }}
                    >
                        {langStrings.configurate}
                    </Button>
                </Box>
            </Box>
        </Paper>
    );
}
